import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import {
  GetUsitcHtsCodesBIZcoreTD,
  WipeUsitcHtsCodesBIZcoreTD,
  FetchUsitcHtsCodesBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import UsitcHtsCodesModal from './UsitcHtsCodesModal';

import global from "../../../../../global.scss";

// Monospace panel for the raw USITC JSON the row carries.
const JsonBlock = ({ title, value, accent = '#3b82f6' }) => {
  const isEmpty = value === null || value === undefined ||
    (typeof value === 'object' && Object.keys(value).length === 0);
  return (
    <Box sx={{ mb: '14px' }}>
      <Typography variant="caption" fontWeight={700}
        sx={{ color: accent, display: 'block', mb: '4px', letterSpacing: '0.3px' }}>
        {title}
      </Typography>
      <Box component="pre" sx={{
        m: 0,
        p: '12px 14px',
        backgroundColor: '#020617',
        border: '1px solid #1e293b',
        borderRadius: '6px',
        color: '#cbd5e1',
        fontSize: '12px',
        lineHeight: 1.6,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxHeight: '340px',
        overflow: 'auto',
      }}>
        {isEmpty ? 'null' : JSON.stringify(value, null, 2)}
      </Box>
    </Box>
  );
};

const UsitcHtsCodesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Code-only lookup, kept separate from the free-text search above.
  const [htsPrefixInput, setHtsPrefixInput] = useState("");
  const [htsPrefix, setHtsPrefix] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [wiping, setWiping] = useState(false);
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [htsCodesList, setHtsCodesList] = useState([]);
  const [htsCodesTotal, setHtsCodesTotal] = useState(0);
  const [expanded, setExpanded] = useState({});

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "", sort: null },
    { label: "HTS code", sort: "hts_code" },
    { label: "Indent", sort: "indent" },
    { label: "Description", sort: "description" },
    { label: "UOM", sort: "primary_uom" },
    { label: "General rate", sort: "general_duty_rate" },
    { label: "Column 2 rate", sort: "column_2_duty_rate" },
    { label: "SPI", sort: null },
    { label: "Revision", sort: null },
    { label: "Last synced", sort: "last_synced_at" },
    { label: "", sort: null },
  ];

  // Clicking a new column starts at ascending; clicking the active one flips it.
  // The old handler flipped direction on every click regardless of column, so
  // switching columns inherited the previous column's direction.
  const handleSort = (value) => {
    if (sort === value) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSort(value);
      setSortDirection("asc");
    }
    handleChangePage(null, 0);
  };

  // Debounce the search so a query fires once the operator stops typing rather
  // than on every keystroke, and reset to the first page — searching from page 40
  // used to land on an empty page.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      handleChangePage(null, 0);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setHtsPrefix(htsPrefixInput);
      handleChangePage(null, 0);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htsPrefixInput]);

  useEffect(() => {
    handleGetHtsCodes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, htsPrefix, sort, sortDirection]);

  // Watch only this slice's list — depending on the whole slice re-ran this on
  // every unrelated action in it.
  useEffect(() => {
    try {
      setHtsCodesList(state.usitcHtsCodesList?.records || []);
      setHtsCodesTotal(state.usitcHtsCodesList?.total || 0);
    } catch (err) {}
  }, [state.usitcHtsCodesList]);

  async function handleGetHtsCodes(customLoading) {
    let query = "";
    if (search) query += `&search=${encodeURIComponent(search)}`;
    if (htsPrefix) query += `&hts_prefix=${encodeURIComponent(htsPrefix)}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetUsitcHtsCodesBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const handleWipe = () => {
    setWipeConfirmOpen(false);
    dispatch(WipeUsitcHtsCodesBIZcoreTD(setWiping)).then(() => {
      handleGetHtsCodes();
    });
  };

  const handleFetch = () => {
    setFetchConfirmOpen(false);
    dispatch(FetchUsitcHtsCodesBIZcoreTD(null, setFetching)).then(() => {
      handleGetHtsCodes();
    });
  };

  const toggleRow = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <Box>

      <UsitcHtsCodesModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetHtsCodes}
      />

      <ConfirmationModal
        open={wipeConfirmOpen}
        setOpen={setWipeConfirmOpen}
        title={"Wipe USITC HTS Codes Table"}
        onCancel={() => setWipeConfirmOpen(false)}
        onConfirm={handleWipe}
      />

      <ConfirmationModal
        open={fetchConfirmOpen}
        setOpen={setFetchConfirmOpen}
        title={"Fetch USITC Tariff Data from USITC"}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={handleFetch}
      />

      <TopBar
        title={"USITC HTS Codes"}
        search={searchInput}
        searchPlaceholder={"Search code, description or duty rate"}
        onSearchChange={(e) => setSearchInput(e.target.value)}
        button3Text={"Wipe table"}
        button3OnClick={() => setWipeConfirmOpen(true)}
        button2Text={fetching ? "Fetching..." : "Fetch data"}
        button2OnClick={() => setFetchConfirmOpen(true)}
        button1Text={"Add HTS code"}
        button1OnClick={() => {
          setUpdate({ defaultMode: 1 });
          setModalTitle("Add USITC HTS code");
          setModalOpen(!modalOpen);
        }}
      />

      {/* Code-only lookup. Anchored to the start of the code and matched on digits,
          so "8502", "8502.1" and "850211" all work while a code that merely contains
          8502 later on is excluded. Separate from the free-text search in the TopBar. */}
      <Box display="flex" alignItems="center" gap="12px" mb="12px" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="8502 or 8502.11.00"
          label="HTS code starts with"
          value={htsPrefixInput}
          onChange={(e) => setHtsPrefixInput(e.target.value.replace(/[^0-9.]/g, ''))}
          inputProps={{ inputMode: 'numeric', style: { fontFamily: 'monospace' } }}
          sx={{ width: '260px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TagOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
              </InputAdornment>
            ),
            endAdornment: htsPrefixInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setHtsPrefixInput('')}>
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {htsPrefix && (
          <Chip
            label={`codes starting ${htsPrefix}`}
            size="small"
            onDelete={() => setHtsPrefixInput('')}
            sx={{ backgroundColor: '#1e3a5f', color: '#93c5fd', fontSize: '11px', height: '22px' }}
          />
        )}
      </Box>

      {!loading && htsCodesList.length > 0 && (
        <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: '8px' }}>
          {htsCodesTotal.toLocaleString()} code{htsCodesTotal === 1 ? '' : 's'}
          {htsPrefix ? ` starting with ${htsPrefix}` : ''}
          {search ? ` matching “${search}”` : ''} · click a row for the full record
        </Typography>
      )}

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          htsCodesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>
              {htsPrefix
                ? `No USITC HTS code starting with ${htsPrefix}`
                : search
                  ? `No USITC HTS code matching “${search}”`
                  : "No USITC HTS code found"}
            </Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sort === heading.sort ? sortDirection : "asc"}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                htsCodesList.map((row, index) => (
                  <React.Fragment key={row.id || index}>
                    <TableRow
                      sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded[row.id] ? 'unset' : undefined } }}
                      onClick={() => toggleRow(row.id)}>
                      <TableCell sx={{ width: '40px' }}>
                        {expanded[row.id]
                          ? <KeyboardArrowUpIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                          : <KeyboardArrowDownIcon sx={{ color: '#64748b', fontSize: 18 }} />}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {row.hts_code || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#64748b">
                          {row.indent != null ? row.indent : <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: "300px" }}>
                        <Typography
                          variant='body2'
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "300px",
                            // Indent mirrors the HTS tree depth so the hierarchy reads
                            // the way it does in the tariff schedule.
                            pl: `${Math.min(row.indent || 0, 6) * 10}px`,
                          }}
                          title={row.description}
                        >
                          {row.description || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#94a3b8" sx={{ whiteSpace: 'nowrap' }}>
                          {row.primary_uom || '—'}{row.secondary_uom ? ` / ${row.secondary_uom}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {row.general_duty_rate != null && row.general_duty_rate !== '' ? row.general_duty_rate : <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#94a3b8" sx={{ whiteSpace: 'nowrap' }}>
                          {row.column_2_duty_rate != null && row.column_2_duty_rate !== '' ? row.column_2_duty_rate : <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(row.spi_allowances || []).length > 0
                          ? <Chip label={`${row.spi_allowances.length} SPI`} size="small"
                              sx={{ backgroundColor: '#1e3a5f', color: '#93c5fd', fontSize: '11px', height: '20px' }} />
                          : <Typography variant="caption" color="#475569">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="#94a3b8">
                          {row.revision_name || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TableTimeStamps updatedAt={row.last_synced_at} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setMenuOpen(true);
                            setUpdate(row);
                            setModalTitle(row.hts_code || "");
                          }}>
                          <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={11} sx={{ py: 0, borderBottom: expanded[row.id] ? '1px solid #334155' : 'none' }}>
                        <Collapse in={!!expanded[row.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ py: '18px' }}>
                            <Box display="flex" gap="28px" flexWrap="wrap" mb="16px">
                              {[
                                { label: 'Record id', value: <Copyable>{row.id}</Copyable> },
                                { label: 'Revision', value: row.revision_name || 'unversioned (legacy)' },
                                { label: 'Revision id', value: row.ref_revision_id ? <Copyable>{row.ref_revision_id}</Copyable> : '—' },
                                { label: 'Indent', value: row.indent != null ? row.indent : '—' },
                                { label: 'Primary UOM', value: row.primary_uom || '—' },
                                { label: 'Secondary UOM', value: row.secondary_uom || '—' },
                              ].map((item, i) => (
                                <Box key={i}>
                                  <Typography variant="caption" color="#64748b" sx={{ display: 'block' }}>{item.label}</Typography>
                                  <Typography variant="body2" color="#e2e8f0" component="div">{item.value}</Typography>
                                </Box>
                              ))}
                            </Box>

                            {row.description && (
                              <Box mb="16px">
                                <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: '2px' }}>
                                  Full description
                                </Typography>
                                <Typography variant="body2" color="#e2e8f0">{row.description}</Typography>
                              </Box>
                            )}

                            <Box display="flex" gap="28px" flexWrap="wrap" mb="16px">
                              <Box>
                                <Typography variant="caption" color="#64748b" sx={{ display: 'block' }}>
                                  Column 1 general rate
                                </Typography>
                                <Typography variant="body2" color="#4ade80" fontWeight={600}>
                                  {row.general_duty_rate || '—'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="#64748b" sx={{ display: 'block' }}>
                                  Column 2 rate (restricted origins)
                                </Typography>
                                <Typography variant="body2" color="#fbbf24" fontWeight={600}>
                                  {row.column_2_duty_rate || '—'}
                                </Typography>
                              </Box>
                            </Box>

                            <JsonBlock title="SPI ALLOWANCES" value={row.spi_allowances} accent="#93c5fd" />
                            <JsonBlock title="FLEX DATA (raw USITC snapshot)" value={row.flex_data} accent="#fbbf24" />
                            <JsonBlock title="META DATA" value={row.meta_data} accent="#64748b" />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={11}
                  count={htsCodesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({ ...update, defaultMode: 2 });
            setMenuOpen(false);
            setModalOpen(!modalOpen);
          }}>
          <Box className="flexCenter_Row" gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default UsitcHtsCodesPage;
